import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Search, Clock, Zap, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import MenuItemCard from './MenuItemCard';
import { CartContext } from '../../../context/CartContext';
import RecentOrdersModal from '../Modals/RecentOrdersModal';
import ShortcutConfirmModal from '../Modals/ShortcutConfirmModal';
import VariantSelectionModal from '../Modals/VariantSelectionModal';
import SpecialInstructionsModal from '../Modals/SpecialInstructionsModal';
import OpenItemModal from '../Modals/OpenItemModal';
import { useMenuItems } from '../../../hooks/useMenuItems';
import toast from 'react-hot-toast';

export default function MenuSection({ browseMode = false, onAddToCart: externalAddToCart, isCounterMode = false }) {
  const { getCurrentCartItems, getRecentOrders, repeatOrder, addToCart: contextAddToCart, currentTableId, isBillPrinted, } =
    useContext(CartContext);

  // Use external addToCart if provided (for Counter mode), otherwise use CartContext
  const addToCart = externalAddToCart || contextAddToCart;

  const { items: apiItems, loading, error, refreshItems } = useMenuItems();

  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecentOrders, setShowRecentOrders] = useState(false);

  // Variant Selection Modal State
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedItemForVariant, setSelectedItemForVariant] = useState(null);

  // Pending item for special instructions before adding to cart
  const [pendingItem, setPendingItem] = useState(null);

  // Open Item Modal State
  const [openItemForModal, setOpenItemForModal] = useState(null);

  // Shortcut System State
  const [quickAddMode, setQuickAddMode] = useState(() => {
    const saved = localStorage.getItem('pos_quickAddMode');
    return saved ? JSON.parse(saved) : true;
  });
  const [shortcutBuffer, setShortcutBuffer] = useState('');
  const [showShortcutIndicator, setShowShortcutIndicator] = useState(false);
  const [highlightedShortcut, setHighlightedShortcut] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [timeoutProgress, setTimeoutProgress] = useState(100);

  const timeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const bufferRef = useRef('');

  // Category scroll navigation
  const categoryScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
  if (Array.isArray(apiItems) && apiItems.length > 0) {
    const mapped = apiItems.map((item) => ({
      ...item,
      price: item.basePrice || 0,
      favorite: item.isFavourite || false,
      // Use the real shortcut key from the backend; fall back to empty string
      // (not an index) so items without one just don't match shortcut typing.
      shortcutKey: item.shortcutKey != null ? item.shortcutKey.toString() : '',
      customizable: true,
      station: item.station || 'kitchen',
    }));
    setMenuItems(mapped);
  }
}, [apiItems]);

  useEffect(() => {
    localStorage.setItem('pos_quickAddMode', JSON.stringify(quickAddMode));
  }, [quickAddMode]);

  const categories = useMemo(() => {
    const allCategories = menuItems.map((item) => item.category).filter(Boolean);
    return ['All', 'Favorites', ...new Set(allCategories)];
  }, [menuItems]);

  const toggleFavorite = (itemId) => {
    setMenuItems((currentItems) =>
      currentItems.map((item) =>
        item.id === itemId ? { ...item, favorite: !item.favorite } : item
      )
    );
  };

  const filteredItems = useMemo(() => {
  const query = searchQuery.toLowerCase().trim();
  return menuItems.filter((item) => {
    const matchesSearch =
      !query ||
      item.name.toLowerCase().includes(query) ||
      item.itemCode?.toLowerCase().includes(query) ||
      item.barcode?.toLowerCase().includes(query) ||
      item.shortcutKey?.toLowerCase().includes(query);


    let matchesCategory =
      selectedCategory === 'All'
        ? true
        : selectedCategory === 'Favorites'
        ? item.favorite
        : item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
}, [selectedCategory, searchQuery, menuItems]);

  const cartItems = getCurrentCartItems() || [];
  const cartItemsMap = cartItems
    .filter((item) => !item.sentToKitchen)
    .reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});

  const recentOrders = getRecentOrders();

  const handleRepeatOrder = (order) => {
    repeatOrder(order);
    setShowRecentOrders(false);
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    const container = categoryScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px threshold
  };

  // Scroll categories left/right
  const scrollCategories = (direction) => {
    const container = categoryScrollRef.current;
    if (!container) return;

    const scrollAmount = 300; // pixels to scroll
    const targetScroll = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  // Check scroll position on mount and when categories change
  useEffect(() => {
    checkScrollPosition();
    const container = categoryScrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Also check on window resize
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [categories]);

  // Toggle: show special instructions popup every time an item is added
  // Set to false to skip the popup and add items directly (instructions can still be edited in cart)
  const showInstructionPopupOnAdd = false;

  // ✅ Minimal default customization - no hardcoded values
  const defaultCustomization = {
    customInstructions: '', // Empty by default - user can add via modal
  };

  // Shortcuts logic (same as before)
  const clearShortcutBuffer = () => {
    bufferRef.current = '';
    setShortcutBuffer('');
    setShowShortcutIndicator(false);
    setHighlightedShortcut(null);
    setTimeoutProgress(100);
    clearTimeout(timeoutRef.current);
    clearInterval(progressIntervalRef.current);
  };

  const processShortcut = (shortcut) => {
      if (isBillPrinted) {
        toast.error('Bill already printed. Order is locked.');
        clearShortcutBuffer();
        return;
    }

    const item = menuItems.find((item) => item.shortcutKey === shortcut);
    if (!item) return clearShortcutBuffer();

    clearShortcutBuffer();

    if (!currentTableId) return;

    // Open item — always show modal (need price input)
    if (item.isOpenItem) {
      setOpenItemForModal(item);
      return;
    }

    // ✅ Check if item has variants - show variant modal
    const hasVariants = item.variants && item.variants.length > 0;
    if (hasVariants) {
      handleItemClick(item);
      return;
    }

    // No variants - add item
    if (quickAddMode) {
      if (showInstructionPopupOnAdd) {
        setPendingItem({ item, showToast: true });
      } else {
        addToCart(item, defaultCustomization, true);
      }
    } else {
      setConfirmItem(item);
    }
  };

  const handleConfirmAdd = () => {
  if (isBillPrinted) {
    toast.error('Bill already printed. Order is locked.');
    setConfirmItem(null);
    return;
  }

    if (confirmItem) {
      if (showInstructionPopupOnAdd) {
        setPendingItem({ item: confirmItem, showToast: true });
      } else {
        addToCart(confirmItem, defaultCustomization, true);
      }
      setConfirmItem(null);
    }
  };

  const handleCancelAdd = () => setConfirmItem(null);

  // Handle open item confirmation — price (and optionally name) entered by staff
  const handleOpenItemConfirm = ({ price, customName }) => {
    if (!openItemForModal) return;
    const item = openItemForModal;
    const openItem = {
      ...item,
      price,
      basePrice: price,
      name: customName || item.name,
      openItemOriginalName: item.name, // preserve original for reporting
    };
    addToCart(openItem, defaultCustomization);
    const label = customName || item.name;
    toast.success(`${label} added — ₹${price}`, { duration: 2000 });
    setOpenItemForModal(null);
  };

  // Handle item click - show variant modal if item has variants, otherwise show instructions popup
  const handleItemClick = (item) => {
      if (isBillPrinted) {
    toast.error('Bill already printed. Order is locked.');
    return;
  }

    // Open item — show price/name input modal
    if (item.isOpenItem) {
      setOpenItemForModal(item);
      return;
    }

    const hasVariants = item.variants && item.variants.length > 0;
    if (hasVariants) {
      setSelectedItemForVariant(item);
      setShowVariantModal(true);
    } else if (showInstructionPopupOnAdd) {
      // Show instructions popup before adding
      setPendingItem({ item, showToast: false });
    } else {
      // Add directly without instructions popup
      addToCart(item, defaultCustomization);
      toast.success(`${item.name} added`, { duration: 2000 });
    }
  };

  // Handle variant selection
  const handleSelectVariant = (variant) => {
      if (isBillPrinted) {
    toast.error('Bill already printed. Order is locked.');
    return;
  }
    if (!selectedItemForVariant) return;

    // Build item with selected variant, then show instructions popup
    const itemWithVariant = {
      ...selectedItemForVariant,
      menuItemId: selectedItemForVariant.id,
      variantId: variant.id,
      variantName: variant.variantName,
      price: variant.variantPrice,
      finalPrice: variant.variantPrice,
    };

    setShowVariantModal(false);
    setSelectedItemForVariant(null);

    if (showInstructionPopupOnAdd) {
      // Show instructions popup after variant selection
      setPendingItem({ item: itemWithVariant, showToast: false });
    } else {
      // Add directly without instructions popup
      addToCart(itemWithVariant, defaultCustomization);
      const label = itemWithVariant.variantName ? `${itemWithVariant.name} (${itemWithVariant.variantName})` : itemWithVariant.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
  };

  // Handle adding item with special instructions
  const handleInstructionsSave = (instructions) => {
    if (!pendingItem) return;
    const { item, showToast } = pendingItem;
    const customization = { customInstructions: instructions || '' };
    addToCart(item, customization, showToast);
    if (!showToast) {
      const label = item.variantName ? `${item.name} (${item.variantName})` : item.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
    setPendingItem(null);
  };

  // Handle closing instructions popup without adding notes (still adds item)
  const handleInstructionsClose = () => {
    if (!pendingItem) return;
    const { item, showToast } = pendingItem;
    addToCart(item, defaultCustomization, showToast);
    if (!showToast) {
      const label = item.variantName ? `${item.name} (${item.variantName})` : item.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
    setPendingItem(null);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Disable shortcuts in browse mode
      if (browseMode) {
        return;
      }

      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable ||
        showRecentOrders ||
        confirmItem ||
        showVariantModal ||
        pendingItem ||
        openItemForModal
      ) {
        return;
      }

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();

        const digit = e.key;
        const newBuffer = bufferRef.current + digit;
        bufferRef.current = newBuffer;
        setShortcutBuffer(newBuffer);
        setShowShortcutIndicator(true);
        setHighlightedShortcut(newBuffer);

        clearTimeout(timeoutRef.current);
        clearInterval(progressIntervalRef.current);

        const exactMatch = menuItems.find((item) => item.shortcutKey === newBuffer);
        const hasPartialMatches = menuItems.some(
          (item) => item.shortcutKey?.startsWith(newBuffer)
        );

        if (exactMatch && !hasPartialMatches) {
          setTimeout(() => processShortcut(newBuffer), 100);
        } else if (exactMatch || hasPartialMatches) {
          setTimeoutProgress(100);
          const startTime = Date.now();
          const duration = 1000;
          progressIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setTimeoutProgress(remaining);
            if (remaining === 0) clearInterval(progressIntervalRef.current);
          }, 16);

          timeoutRef.current = setTimeout(() => {
            processShortcut(bufferRef.current);
          }, 1000);
        } else {
          clearShortcutBuffer();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      clearShortcutBuffer();
    };
  }, [menuItems, quickAddMode, showRecentOrders, confirmItem, showVariantModal, pendingItem, currentTableId, browseMode]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* SEARCH BAR & QUICK ACTIONS */}
      <div className="border-b border-slate-300 px-6 py-4">
        <div className="flex gap-3 items-center">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <input
  type="text"
  placeholder="Search menu items..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  className="w-full pl-10 pr-4 py-2 border border-slate-300 hover:border-slate-400 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 text-sm rounded-lg"
/>
          </div>

          {/* Quick Add Mode Toggle - Hidden in browse mode */}
          {!browseMode && (
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <button
                onClick={() => setQuickAddMode(!quickAddMode)}
                className={`flex items-center gap-2 px-3 py-1 rounded-md font-semibold text-xs transition-all ${
                  quickAddMode ? 'bg-teal-600 text-white' : 'bg-amber-500 text-white'
                }`}
              >
                {quickAddMode ? (
                  <>
                    <Zap size={14} /> <span>Quick Add</span>
                  </>
                ) : (
                  <>
                    <Shield size={14} /> <span>Safe Mode</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Refresh Button */}
          <button
            onClick={refreshItems}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all font-semibold text-sm"
          >
            Refresh
          </button>

          {/* Recent Orders Button */}
          <button
            onClick={() => setShowRecentOrders(true)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all font-semibold text-sm flex items-center gap-2 border border-purple-200"
          >
            <Clock size={16} />
            Recent
          </button>
        </div>
      </div>

      {/* CATEGORIES - Clean Navigation Style */}
      <div className="border-b border-slate-200 bg-slate-50">
        <style>{`
          .category-scroll::-webkit-scrollbar {
            display: none;
          }
          .category-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        <div className="flex items-center">
          {/* Left Arrow - Integrated */}
          <button
            onClick={() => scrollCategories('left')}
            className={`
              flex-shrink-0 w-10 h-11 flex items-center justify-center border-r border-slate-200 transition-all
              ${canScrollLeft
                ? 'text-teal-600 hover:bg-teal-50 cursor-pointer'
                : 'text-slate-300 cursor-not-allowed'}
            `}
            disabled={!canScrollLeft}
            aria-label="Scroll categories left"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Categories Container */}
          <div
            ref={categoryScrollRef}
            className="flex-1 overflow-x-auto overflow-y-hidden category-scroll"
          >
            <div className="flex items-center justify-start gap-1 px-3 py-2 min-w-max">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSearchQuery('');
                  }}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-lg whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Right Arrow - Integrated */}
          <button
            onClick={() => scrollCategories('right')}
            className={`
              flex-shrink-0 w-10 h-11 flex items-center justify-center border-l border-slate-200 transition-all
              ${canScrollRight
                ? 'text-teal-600 hover:bg-teal-50 cursor-pointer'
                : 'text-slate-300 cursor-not-allowed'}
            `}
            disabled={!canScrollRight}
            aria-label="Scroll categories right"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* ITEMS GRID */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <p className="text-center text-slate-500">Loading menu items...</p>
        ) : error ? (
          <p className="text-center text-red-500">Failed to load menu items: {error}</p>
        ) : filteredItems.length > 0 ? (
          <div className="grid grid-cols-5 gap-4">
            {filteredItems.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                cartItem={cartItemsMap[item.id]}
                isFavorite={item.favorite}
                onToggleFavorite={() => toggleFavorite(item.id)}
                isHighlighted={highlightedShortcut && item.shortcutKey.startsWith(highlightedShortcut)}
                onItemClick={handleItemClick}
                browseMode={browseMode}
                disabled={isBillPrinted}   
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-slate-500 font-semibold">No items found</p>
          </div>
        )}
      </div>

      {/* Recent Orders Modal */}
      {showRecentOrders && (
        <RecentOrdersModal
          currentTableId={currentTableId}
          onClose={() => setShowRecentOrders(false)}
          onAddToCart={(items) => {
            // Use repeatOrder to add items to current order
            if (items && items.length > 0) {
              const expandedItems = [];
              items.forEach(item => {
                const quantity = item.quantity || 1;
                for (let i = 0; i < quantity; i++) {
                  expandedItems.push({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    finalPrice: item.price,
                    variantId: item.variantId,
                    variantName: item.variantName,
                    customization: item.customization,
                    quantity: 1,
                    sentToKitchen: false,
                  });
                }
              });
              repeatOrder({ items: expandedItems });
            }
            setShowRecentOrders(false);
          }}
        />
      )}

      {/* Shortcut Confirmation Modal */}
      {confirmItem && (
        <ShortcutConfirmModal item={confirmItem} onConfirm={handleConfirmAdd} onCancel={handleCancelAdd} />
      )}

      {/* Variant Selection Modal */}
      {showVariantModal && selectedItemForVariant && (
        <VariantSelectionModal
          item={selectedItemForVariant}
          onSelectVariant={handleSelectVariant}
          onClose={() => {
            setShowVariantModal(false);
            setSelectedItemForVariant(null);
          }}
        />
      )}

      {/* Special Instructions Modal - shown before adding item to cart */}
      {pendingItem && (
        <SpecialInstructionsModal
          item={pendingItem.item}
          onSave={handleInstructionsSave}
          onClose={handleInstructionsClose}
        />
      )}

      {/* Open Item Modal - price/name input for open items */}
      {openItemForModal && (
        <OpenItemModal
          item={openItemForModal}
          onConfirm={handleOpenItemConfirm}
          onClose={() => setOpenItemForModal(null)}
        />
      )}
    </div>
  );
}
